import * as Excel from 'exceljs'
import moment from 'moment'

type ContentT = {
    projectID: string
    deletedDate: string
    remarkDeleted: string
    projectType: string
    researcherName: string
    projectNameTH: string
    projectNameEN: string
}
type ExportProps = {
    header: {
        keyword: string
    }
    content: ContentT[]
    translate: (value: string) => string
}
const deletedProjectExport = ({ header, content, translate }: ExportProps) => {
    let startRowHeader = 5
    const workbook = new Excel.Workbook()
    const worksheet = workbook.addWorksheet('sheet', {
        pageSetup: {
            paperSize: 9,
            orientation: 'portrait',
        },
    })
    workbook.creator = 'CU_IRB-WEB'
    workbook.lastModifiedBy = 'CU_IRB-WEB'

    const createTopic = () => {
        const endCell = 'G'
        worksheet.mergeCells(`A1:${endCell}1`)
        worksheet.getCell('A1').value = translate('EXPORT.EXCEL.DELETED_PROJECT_NAME')
        worksheet.getCell('A1').alignment = { horizontal: 'center' }
        worksheet.getCell('A1').font = { bold: true }
        worksheet.getCell('A1').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: {
                argb: '00F1BBCF',
            },
        }

        worksheet.mergeCells(`A3:B3`)
        worksheet.getCell('A3').value = `${translate('EXPORT.EXCEL.KEYWORD')} ${header.keyword}`
        worksheet.getCell('A3').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '00F79646' } }
    }

    const setCellBorder = (colKey: string) => {
        worksheet.getCell(colKey).border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
        }
    }

    const createHeader = () => {
        let arrHeader = [
            {
                name: translate('EXPORT.EXCEL.PROJECT-ID'),
                key: 'projectID',
                width: 10,
            },
            {
                name: translate('EXPORT.EXCEL.DELETED_DATE'),
                key: 'deletedDate',
                width: 20,
            },
            {
                name: translate('EXPORT.EXCEL.DELETED_REMARK'),
                key: 'remarkDeleted',
                width: 20,
            },
            {
                name: translate('EXPORT.EXCEL.RESEARCHER-NAME'),
                key: 'researcherName',
                width: 50,
            },
            {
                name: translate('EXPORT.EXCEL.PROJECT-TYPE'),
                key: 'projectType',
                width: 20,
            },
            {
                name: translate('EXPORT.EXCEL.PROJECT-NAME-TH'),
                key: 'projectNameTH',
                width: 50,
            },
            {
                name: translate('EXPORT.EXCEL.PROJECT-NAME-EN'),
                key: 'projectNameEN',
                width: 50,
            },
        ]

        worksheet.columns = arrHeader.map((itm) => ({ key: itm.key, width: itm.width }))
        worksheet.getRow(startRowHeader).values = arrHeader.map((itm) => itm.name)
        ;[
            `A${startRowHeader}`,
            `B${startRowHeader}`,
            `C${startRowHeader}`,
            `D${startRowHeader}`,
            `E${startRowHeader}`,
            `F${startRowHeader}`,
            `G${startRowHeader}`,
        ].forEach((colKey, index) => {
            if (index < 10) {
                setCellBorder(colKey)
                worksheet.getCell(colKey).alignment = { vertical: 'middle', horizontal: 'center', wrapText: true }
                worksheet.getCell(colKey).fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: {
                        argb: '00DB5F8E',
                    },
                }
            }
        })
    }

    const setCellRight = (colKey: string) => {
        worksheet.getCell(colKey).alignment = { horizontal: 'right' }
    }

    const setCellCenter = (colKey: string) => {
        worksheet.getCell(colKey).alignment = { horizontal: 'center' }
    }
    const setContentBorder = (colKey: string) => {
        worksheet.getCell(colKey).border = {
            left: { style: 'thin' },
            right: { style: 'thin' },
        }
    }

    const setLastContentBorder = (colKey: string) => {
        worksheet.getCell(colKey).border = {
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
        }
    }

    const setContentColor = (colKey: string) => {
        worksheet.getCell(colKey).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: {
                argb: '00F1BBCF',
            },
        }
    }

    const makeRowITM = (rowNumber: number, rowITM: ContentT & { isOddRow: boolean; lastRow: boolean }) => {
        worksheet.addRow(rowITM)

        const arrColKey = [
            `A${rowNumber}`,
            `B${rowNumber}`,
            `C${rowNumber}`,
            `D${rowNumber}`,
            `E${rowNumber}`,
            `F${rowNumber}`,
            `G${rowNumber}`,
        ]

        arrColKey.forEach((colKey, index) => {
            if (index < 7) {
                if (index === 0) {
                    setCellRight(colKey)
                }
                if ([1, 2].includes(index)) {
                    setCellCenter(colKey)
                }

                setContentBorder(colKey)
                if (rowITM.isOddRow) {
                    setContentColor(colKey)
                }
                if (rowITM.lastRow) {
                    setLastContentBorder(colKey)
                }
            }
        })
    }

    const createBody = () => {
        try {
            content.forEach((rowGroupITM, index) => {
                makeRowITM(index + (startRowHeader + 1), {
                    lastRow: content.length === index + 1 ? true : false,
                    isOddRow: (index + 1) % 2 === 0 ? false : true,
                    ...rowGroupITM,
                })
            })
        } catch (err: any) {
            console.log('Error Generate Row Excel file export: ', err)
            throw new Error(err)
        }
    }

    const createTable = () => {
        createHeader()
        createBody()
    }

    const processColumns = () => {
        createTopic()
        createTable()
    }

    processColumns()
    workbook.xlsx.writeBuffer().then(function (xls64data) {
        const blob = new Blob([xls64data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        const url = window.URL.createObjectURL(blob)
        const anchor = document.createElement('a')

        const reportDate = moment(new Date()).format('yyyyMMDD')

        anchor.href = url
        anchor.download = `${translate('EXPORT.EXCEL.DELETED_PROJECT_NAME')}_${reportDate}.xlsx`
        //@ts-ignore

        anchor.click()

        window.URL.revokeObjectURL(url)
    })
}

export default deletedProjectExport
